import React from "react";
import { styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Button,
  Typography,
  TextField,
  Slider,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Box,
  IconButton,
} from "@mui/material";
import { useRecordAudio } from "../utils/utils";
import clsx from "clsx";
import { AudioPlayerControlSprite, Audio } from "react-audio-player-pro";
import "react-audio-player-pro/dist/style.css";
import { radiusMarks, themeFilters } from "../constants";
import { updateElement } from "../../../reducers/configurator/configuratorSlice";
import {
  ArrowBack,
  ArrowForward,
  ArrowDownward,
  ArrowUpward,
} from "@mui/icons-material";

const PREFIX = "ThemesDetailsContainer";

const classes = {
  themeFieldsContainer: `${PREFIX}-themeFieldsContainer`,
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  musicContainer: `${PREFIX}-musicContainer`,
  recordTimer: `${PREFIX}-recordTimer`,
  musicTitleEllipsis: `${PREFIX}-musicTitleEllipsis`,
  audioTimer: `${PREFIX}-audioTimer`,
  buttonsContainer: `${PREFIX}-buttonsContainer`,
  importSongButton: `${PREFIX}-importSongButton`,
  checkboxLoop: `${PREFIX}-checkboxLoop`,
  dividerStyle: `${PREFIX}-dividerStyle`,
  minWidthContainer: `${PREFIX}-minWidthContainer`,
  sliderContainer: `${PREFIX}-sliderContainer`,
  themesContainer: `${PREFIX}-themesContainer`,
  noThemeText: `${PREFIX}-noThemeText`,
  areaMusicTitle: `${PREFIX}-areaMusicTitle`,
  audioTopContainer: `${PREFIX}-audioTopContainer`,
  importSongButtonInPreview: `${PREFIX}-importSongButtonInPreview`,
  songButtons: `${PREFIX}-songButtons`,
  musicTitleEllipsisInPreview: `${PREFIX}-musicTitleEllipsisInPreview`,
  audioContainerInPreview: `${PREFIX}-audioContainerInPreview`,
  sliderPaddingInPreview: `${PREFIX}-sliderPaddingInPreview`,
  areaOfEffectText: `${PREFIX}-areaOfEffectText`,
  autocompleteFilterPreview: `${PREFIX}-autocompleteFilterPreview`,
  autocompleteFilter: `${PREFIX}-autocompleteFilter`,
  marginRight: `${PREFIX}-marginRight`,
  areaFilterContainer: `${PREFIX}-areaFilterContainer`,
  iconButtonStyle: `${PREFIX}-iconButtonStyle`,
  selectedOffset: `${PREFIX}-selectedOffset`,
  marginRightOffsets: `${PREFIX}-marginRightOffsets`,
};

const ThemesDetailsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.themeFieldsContainer}`]: {
      margin: "2rem 0 1rem 0",
      width: "300px",
      [theme.breakpoints.between("xs", "sm")]: {
        width: "100%",
      },
    },
    [`& .${classes.themesContainer}`]: {
      display: "flex",
      flexDirection: "column",
      maxHeight: "245px",
      overflowY: "auto",
      paddingRight: "1rem",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.marginBottom}`]: {
      marginBottom: "1rem",
    },
    [`& .${classes.noThemeText}`]: {
      margin: "3rem auto !important",
    },
    [`& .${classes.marginTop}`]: {
      marginTop: "1rem",
    },
    [`& .${classes.musicContainer}`]: {
      marginBottom: "1rem !important",
    },
    [`& .${classes.recordTimer}`]: {
      display: "flex",
      justifyContent: "center",
      marginBottom: "1rem",
    },
    [`& .${classes.musicTitleEllipsis}`]: {
      maxWidth: "275px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    [`& .${classes.buttonsContainer}`]: {
      display: "flex",
      justifyContent: "center",
    },
    [`& .${classes.importSongButton}`]: {
      display: "flex",
      justifyContent: "center",
      height: "36px",
    },
    [`& .${classes.checkboxLoop}`]: {
      display: "flex",
      justifyContent: "center",
    },
    [`& .${classes.dividerStyle}`]: {
      height: "1px",
      backgroundColor: "grey",
      opacity: "0.8",
      marginBottom: "1rem",
    },
    [`& .${classes.minWidthContainer}`]: {
      width: "300px",
    },
    [`& .${classes.sliderContainer}`]: {
      "& .MuiSlider-thumb": {
        marginTop: "0",
        marginLeft: "0",
      },
    },
    [`& .${classes.areaMusicTitle}`]: {
      marginBottom: "0.5rem !important",
    },
    [`& .${classes.audioTopContainer}`]: {
      position: "relative",
    },
    [`& .${classes.importSongButtonInPreview}`]: {
      display: "flex",
      justifyContent: "space-between",
    },
    [`& .${classes.songButtons}`]: {
      display: "flex",
      marginBottom: "0.5rem",
    },
    [`& .${classes.musicTitleEllipsisInPreview}`]: {
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    [`& .${classes.audioContainerInPreview}`]: {
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "& > div": {
        backgroundColor: "inherit",
      },
    },
    [`& .${classes.sliderPaddingInPreview}`]: {
      padding: "0 1rem",
      "& .MuiSlider-thumb": {
        marginTop: "0",
        marginLeft: "0",
      },
    },
    [`& .${classes.areaOfEffectText}`]: {
      position: "relative",
      left: "-1rem",
      top: "0",
    },
    [`& .${classes.autocompleteFilterPreview}`]: {
      marginTop: "1rem",
      display: "flex",
    },
    [`& .${classes.autocompleteFilter}`]: {
      width: "100%",
    },
    [`& .${classes.marginRight}`]: {
      marginRight: "0.5rem",
    },
    [`& .${classes.areaFilterContainer}`]: {
      display: "flex",
      justifyContent: "space-between",
    },
    [`& .${classes.iconButtonStyle}`]: {
      marginRight: "0.5rem !important",
      padding: 0,
    },
    [`& .${classes.selectedOffset}`]: {
      backgroundColor: theme.palette.primary.main,
      "& > svg": {
        fill: "#303030",
      },
    },
    [`& .${classes.marginRightOffsets}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        marginRight: "0",
      },
      marginRight: "0.5rem",
    },
  };
});

const ThemesDetails = (props) => {
  const { selectedObject, presetState, setPresetState, creation } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const selectedTile = useSelector((state) =>
    currentTile
      ? state.configurator.grid[parentLayout][currentTile.row][
          currentTile.column
        ]
      : null
  );

  const handleThemeChange = (key, value) => {
    const tmp = { ...selectedTile.theme };
    if (key === "song") {
      tmp.song = value.file;
      tmp.preview = value.preview;
    } else tmp[key] = value;
    dispatch(
      updateElement({
        layer: "theme",
        data: tmp,
        tile: { row: currentTile.row, column: currentTile.column },
      })
    );
  };

  const myRecordAudio = useRecordAudio(
    creation ? presetState : null,
    creation ? setPresetState : handleThemeChange,
    creation ? true : false
  );

  const handleAudioName = (url) => {
    if (url.length > 0) {
      if (url.includes("defaultADSSong.mp3"))
        return t("configurator.defaultMusic");
      let tmp = url.substr(8, url.length).split("/");
      return tmp[tmp.length - 1];
    } else return "";
  };

  const centerSpawn = (key, direction) => {
    const myData = { ...selectedTile.theme };
    if (selectedTile.theme[key] === direction) {
      delete myData[key];
    } else {
      myData[key] = direction;
    }
    dispatch(
      updateElement({
        layer: "theme",
        data: myData,
        tile: { row: currentTile.row, column: currentTile.column },
      })
    );
  };

  return (
    <ThemesDetailsContainer>
      <div
        className={
          !creation
            ? classes.themesContainer
            : creation && !selectedTile?.theme?.radius
            ? classes.themeFieldsContainer
            : ""
        }
      >
        {!creation &&
        !selectedTile?.theme?.theme &&
        !selectedTile?.theme?.song ? (
          <Typography variant="body1" className={classes.noThemeText}>
            {t("configurator.noThemeText")}
          </Typography>
        ) : (
          <>
            {(selectedTile.theme?.song || selectedTile.theme?.theme) && (
              <>
                {/* <Divider
                  className={clsx(classes.dividerStyle, classes.marginTop)}
                /> */}
                <FormControl sx={{ marginBottom: "0.5rem" }}>
                  <FormLabel id="select-theme-on-radio-buttons-group-label">
                    {t("configurator.themeAreaTypeSelectorTitle")}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="select-theme-on-radio-buttons-group-label"
                    name="select-theme-on-radio-buttons-group"
                    value={selectedTile.theme.areaMode}
                    onChange={(e) =>
                      handleThemeChange("areaMode", e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="radius"
                      control={<Radio />}
                      label={t("configurator.themeAreaTypeRound")}
                    />
                    <FormControlLabel
                      value="scaled"
                      control={<Radio />}
                      label={t("configurator.themeAreaTypeScaled")}
                    />
                  </RadioGroup>
                </FormControl>
                <div className={classes.sliderPaddingInPreview}>
                  {selectedTile.theme.areaMode === "radius" ? (
                    <>
                      <Typography
                        variant="body1"
                        className={classes.areaOfEffectText}
                      >
                        {t("configurator.areaOfEffect")}
                      </Typography>
                      <Slider
                        key={`slider-radius-in-preview-${selectedTile.theme.theme}-${selectedTile.row}-${selectedTile.col}`}
                        value={selectedTile.theme.radius}
                        onChange={(e, value) => {
                          if (selectedTile.theme.radius !== value)
                            handleThemeChange("radius", value);
                        }}
                        getAriaValueText={(value) => `${value}m`}
                        aria-labelledby="discrete-slider-radius-in-preview"
                        step={0.5}
                        marks={radiusMarks}
                        min={1}
                        max={5}
                        valueLabelDisplay="auto"
                      />
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-around",
                      }}
                    >
                      <TextField
                        label={t("configurator.sizeX")}
                        type="number"
                        sx={{ width: "70px" }}
                        value={selectedTile.theme.scaleX || 1}
                        className={classes.marginBottom}
                        onChange={(e) =>
                          handleThemeChange("scaleX", e.target.value)
                        }
                        variant={"outlined"}
                        InputProps={{
                          inputProps: { min: 0.5, max: 10, step: 0.5 },
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <TextField
                        label={t("configurator.sizeY")}
                        type="number"
                        sx={{ width: "70px" }}
                        value={selectedTile.theme.scaleY || 1}
                        className={classes.marginBottom}
                        onChange={(e) =>
                          handleThemeChange("scaleY", e.target.value)
                        }
                        variant={"outlined"}
                        InputProps={{
                          inputProps: { min: 0.5, max: 10, step: 0.5 },
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          textAlign: "center",
                          marginLeft: "0.5rem",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ marginBottom: "0.25rem" }}
                        >
                          {t("configurator.offsetTitle")}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                          <IconButton
                            color="primary"
                            aria-label="offset spawn top"
                            component="span"
                            className={clsx(
                              classes.iconButtonStyle,
                              classes.marginRightOffsets,
                              selectedTile.theme.offsetY === "top"
                                ? classes.selectedOffset
                                : ""
                            )}
                            onClick={() => {
                              centerSpawn("offsetY", "top");
                            }}
                          >
                            <ArrowUpward fontSize="medium" />
                          </IconButton>
                          <IconButton
                            color="primary"
                            aria-label="offset span bottom"
                            component="span"
                            className={clsx(
                              classes.iconButtonStyle,
                              classes.marginRightOffsets,
                              selectedTile.theme.offsetY === "bottom" &&
                                classes.selectedOffset
                            )}
                            onClick={() => centerSpawn("offsetY", "bottom")}
                          >
                            <ArrowDownward fontSize="medium" />
                          </IconButton>
                          <IconButton
                            color="primary"
                            aria-label="offset span right"
                            component="span"
                            className={clsx(
                              classes.iconButtonStyle,
                              classes.marginRightOffsets,
                              selectedTile.theme.offsetX === "right" &&
                                classes.selectedOffset
                            )}
                            onClick={() => centerSpawn("offsetX", "right")}
                          >
                            <ArrowForward fontSize="medium" />
                          </IconButton>

                          <IconButton
                            color="primary"
                            aria-label="offset span left"
                            component="span"
                            className={clsx(
                              classes.iconButtonStyle,
                              classes.marginRightOffsets,
                              selectedTile.theme.offsetX === "left" &&
                                classes.selectedOffset
                            )}
                            onClick={() => centerSpawn("offsetX", "left")}
                          >
                            <ArrowBack fontSize="medium" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </div>
              </>
            )}
            {creation ? (
              <>
                {(selectedObject.value === "song" ||
                  selectedObject.value === "songAndFilter") && (
                  <div className={classes.musicContainer}>
                    <div>
                      <FormControl>
                        <FormLabel id="select-theme-on-radio-buttons-group-label">
                          {t("configurator.themeAreaTypeSelectorTitle")}
                        </FormLabel>
                        <RadioGroup
                          row
                          aria-labelledby="select-theme-on-radio-buttons-group-label"
                          name="select-theme-on-radio-buttons-group"
                          value={presetState.areaMode}
                          onChange={(e) =>
                            setPresetState({
                              ...presetState,
                              areaMode: e.target.value,
                            })
                          }
                        >
                          <FormControlLabel
                            value="radius"
                            control={<Radio />}
                            label={t("configurator.themeAreaTypeRound")}
                          />
                          <FormControlLabel
                            value="scaled"
                            control={<Radio />}
                            label={t("configurator.themeAreaTypeScaled")}
                          />
                        </RadioGroup>
                      </FormControl>
                      {presetState.areaMode === "radius" ? (
                        <div className={classes.sliderContainer}>
                          <Typography
                            variant="body1"
                            className={classes.minWidthContainer}
                          >
                            {t("configurator.areaOfEffect")}
                          </Typography>
                          <Slider
                            key={`slider-radius-${selectedObject.value}-${selectedTile.row}-${selectedTile.col}`}
                            value={presetState.radius}
                            onChange={(e, value) => {
                              if (presetState.radius !== value)
                                setPresetState({
                                  ...presetState,
                                  radius: value,
                                });
                            }}
                            getAriaValueText={(value) => `${value}m`}
                            aria-labelledby="discrete-slider-radius"
                            step={0.5}
                            marks={radiusMarks}
                            min={1}
                            max={5}
                            valueLabelDisplay="auto"
                          />
                        </div>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-around",
                          }}
                        >
                          <TextField
                            label={t("configurator.sizeX")}
                            type="number"
                            sx={{ width: "70px" }}
                            value={presetState.scaleX || 1}
                            className={classes.marginBottom}
                            onChange={(e) =>
                              setPresetState({
                                ...presetState,
                                scaleX: e.target.value,
                              })
                            }
                            variant={"outlined"}
                            InputProps={{
                              inputProps: { min: 0.5, max: 10, step: 0.5 },
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                          <TextField
                            label={t("configurator.sizeY")}
                            type="number"
                            sx={{ width: "70px" }}
                            value={presetState.scaleY || 1}
                            className={classes.marginBottom}
                            onChange={(e) =>
                              setPresetState({
                                ...presetState,
                                scaleY: e.target.value,
                              })
                            }
                            variant={"outlined"}
                            InputProps={{
                              inputProps: { min: 0.5, max: 10, step: 0.5 },
                            }}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Box>
                      )}
                      <Typography
                        variant="body1"
                        className={
                          creation
                            ? classes.marginBottom
                            : classes.areaMusicTitle
                        }
                        sx={{ marginTop: "1rem" }}
                      >
                        {creation
                          ? t("configurator.themeInfos")
                          : t("configurator.areaInfos")}
                      </Typography>
                      <TextField
                        name={`title-theme`}
                        label={t("configurator.themeTitle")}
                        value={
                          creation
                            ? presetState.title
                            : selectedTile.theme?.title
                        }
                        onChange={(e) =>
                          creation
                            ? setPresetState({
                                ...presetState,
                                title: e.target.value,
                              })
                            : handleThemeChange("title", e.target.value)
                        }
                        className={clsx(
                          classes.marginBottom,
                          !creation ? classes.marginTop : ""
                        )}
                        fullWidth
                        variant={"outlined"}
                      />
                      <TextField
                        name={`description-theme`}
                        label={t("configurator.themeDescription")}
                        multiline
                        value={
                          creation
                            ? presetState.description
                            : selectedTile.theme?.description
                        }
                        onChange={(e) =>
                          creation
                            ? setPresetState({
                                ...presetState,
                                description: e.target.value,
                              })
                            : handleThemeChange("description", e.target.value)
                        }
                        fullWidth
                        variant={"outlined"}
                      />
                      {!creation && (
                        <Divider
                          className={clsx(
                            classes.dividerStyle,
                            classes.marginTop
                          )}
                        />
                      )}
                    </div>
                    <Typography
                      variant="body1"
                      className={clsx(classes.marginBottom, classes.marginTop)}
                    >
                      {t("configurator.addAmbiantMusic")}
                    </Typography>
                    {myRecordAudio.isRecording && (
                      <Typography variant="h5" className={classes.recordTimer}>
                        {myRecordAudio.timer}
                      </Typography>
                    )}
                    {presetState.preview && !myRecordAudio.isRecording && (
                      <Typography
                        variant="body1"
                        className={classes.musicTitleEllipsis}
                      >
                        {presetState.song[0].name}
                      </Typography>
                    )}
                    {presetState.preview && !myRecordAudio.isRecording && (
                      <div className={classes.marginBottom}>
                        <AudioPlayerControlSprite />
                        <Audio
                          // string - path to audio file, required
                          src={presetState.preview}
                          // string - 'none' | 'metadata' | 'auto', default: 'auto', optional
                          preload="auto"
                          // string - name for download file, optional, deafult:
                          downloadFileName={presetState.preview}
                        />
                      </div>
                    )}
                    <div className={classes.buttonsContainer}>
                      {!myRecordAudio.isRecording && (
                        <Button
                          variant="contained"
                          color="primary"
                          component="label"
                          className={classes.importSongButton}
                          onChange={(e) => {
                            const mySong = URL.createObjectURL(
                              e.target.files[0]
                            );
                            setPresetState({
                              ...presetState,
                              song: e.target.files,
                              preview: mySong,
                            });
                          }}
                        >
                          <input
                            type="file"
                            accept=".mp3,.wav,.ogg"
                            name="radiusMusic"
                            hidden
                          />
                          {t("configurator.importButton")}
                        </Button>
                      )}
                      {myRecordAudio.recordButton}
                    </div>
                    {presetState.preview && !myRecordAudio.isRecording && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            color={"primary"}
                            onChange={(e) =>
                              setPresetState({
                                ...presetState,
                                loop: e.target.checked,
                              })
                            }
                            checked={presetState.loop}
                          />
                        }
                        label={t("configurator.loop")}
                        className={clsx(classes.checkboxLoop)}
                        name={`loop-audio`}
                      />
                    )}
                  </div>
                )}
                {selectedObject.value !== "song" && (
                  <div className={classes.marginBottom}>
                    {selectedObject.value === "songAndFilter" && (
                      <>
                        <Divider className={classes.dividerStyle} />
                        <Typography
                          variant="body1"
                          className={classes.marginBottom}
                        >
                          {t("configurator.addFilter")}
                        </Typography>
                        <Autocomplete
                          id="select-filter-to-set"
                          options={themeFilters(t)}
                          getOptionLabel={(option) => option.title}
                          disableClearable
                          onChange={(e, selected) => {
                            if (selected) {
                              setPresetState({
                                ...presetState,
                                theme: selected.value,
                              });
                            } else
                              props.setSelectedObject({
                                ...presetState,
                                theme: null,
                              });
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("configurator.autocompleteSelectfilter")}
                              variant="outlined"
                            />
                          )}
                        />
                      </>
                    )}
                  </div>
                )}
                {/* {(selectedObject.value === "song" ||
                  selectedObject.value === "songAndFilter") && (
                  <Divider className={classes.dividerStyle} />
                )} */}
              </>
            ) : (
              <>
                {selectedTile.theme?.song && (
                  <>
                    <Typography
                      variant="body1"
                      className={classes.areaMusicTitle}
                    >
                      {t("configurator.areaMusic")}
                    </Typography>
                    <div className={classes.audioTopContainer}>
                      {myRecordAudio.isRecording && (
                        <Typography
                          variant="h5"
                          className={classes.recordTimer}
                        >
                          {myRecordAudio.timer}
                        </Typography>
                      )}
                      <div className={classes.importSongButtonInPreview}>
                        {myRecordAudio.recordButton}
                        {!myRecordAudio.isRecording && (
                          <div className={classes.songButtons}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color={"primary"}
                                  onChange={(e) =>
                                    handleThemeChange("loop", e.target.checked)
                                  }
                                  checked={selectedTile.theme?.loop}
                                />
                              }
                              label={t("configurator.loop")}
                              className={clsx(classes.checkboxLoop)}
                              name={`loop-audio`}
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              component="label"
                              className={
                                selectedTile.theme?.song
                                  ? classes.marginRight
                                  : ""
                              }
                              onChange={(e) => {
                                const mySong = URL.createObjectURL(
                                  e.target.files[0]
                                );
                                handleThemeChange("song", {
                                  file: e.target.files,
                                  preview: mySong,
                                });
                              }}
                            >
                              <input
                                type="file"
                                accept=".mp3,.wav,.ogg"
                                name="radiusMusic"
                                hidden
                              />
                              {t("configurator.importButton")}
                            </Button>
                            {selectedTile.theme?.song && (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                component="label"
                                onClick={() =>
                                  handleThemeChange(
                                    "song",
                                    { file: null, preview: null },
                                    true
                                  )
                                }
                              >
                                {t("configurator.removeButton")}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      {!myRecordAudio.isRecording && (
                        <>
                          <Typography
                            variant="body1"
                            className={classes.musicTitleEllipsisInPreview}
                          >
                            {typeof selectedTile.theme.song === "string" ||
                            selectedTile.theme.song instanceof String
                              ? handleAudioName(selectedTile.theme.song)
                              : selectedTile.theme.song[0].name}
                          </Typography>
                          <div
                            className={clsx(
                              classes.audioContainerInPreview,
                              classes.marginBottom
                            )}
                          >
                            <AudioPlayerControlSprite />
                            <Audio
                              // string - path to audio file, required
                              src={
                                selectedTile.theme.preview
                                  ? selectedTile.theme.preview
                                  : selectedTile.theme.song
                              }
                              // string - 'none' | 'metadata' | 'auto', default: 'auto', optional
                              preload="auto"
                              // string - name for download file, optional, deafult:
                              downloadFileName={
                                selectedTile.theme.preview
                                  ? selectedTile.theme.preview
                                  : selectedTile.theme.song
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
                {selectedTile.theme?.theme && (
                  <>
                    {selectedTile.theme?.song && (
                      <Divider
                        className={clsx(
                          classes.dividerStyle,
                          classes.marginTop
                        )}
                      />
                    )}
                    <div className={classes.areaFilterContainer}>
                      <Typography variant="body1">
                        {t("configurator.areafilter")}
                      </Typography>
                      {selectedTile.theme?.song && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          component="label"
                          onClick={() => handleThemeChange("theme", null, true)}
                        >
                          {t("configurator.removeButton")}
                        </Button>
                      )}
                    </div>
                    <div className={classes.autocompleteFilterPreview}>
                      <Autocomplete
                        id="select-filter-to-set-preview"
                        className={classes.autocompleteFilter}
                        options={themeFilters(t)}
                        getOptionLabel={(option) => option.title}
                        value={
                          themeFilters(t).filter(
                            (item) => item.value === selectedTile.theme.theme
                          )[0]
                        }
                        inputValue={
                          themeFilters(t).filter(
                            (item) => item.value === selectedTile.theme.theme
                          )[0].title
                        }
                        disableClearable
                        onChange={(e, selected) => {
                          if (selected) {
                            handleThemeChange("theme", selected.value);
                          } else handleThemeChange("song", null, true);
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("configurator.autocompleteSelectfilter")}
                            variant="outlined"
                          />
                        )}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </ThemesDetailsContainer>
  );
};

export default ThemesDetails;
